import { Ray } from '../../math/Ray';
import { HeightField, HeightFieldHit, HeightFieldSample } from './HeightField';
import { centralDifferenceNormal, raycastHeightField } from './heightfield-raycast';

export interface HeightmapGrid {
  /** RGBA8 pixel buffer (only the R channel is used). */
  data: Uint8Array;
  width: number;
  height: number;
}

export interface HeightmapHeightFieldConfig {
  heightmap: HeightmapGrid;
  amplitude: number;
  worldMinX: number;
  worldMinZ: number;
  worldWidth: number;
  worldDepth: number;
  /**
   * When > 0, the field snaps to the per-tile vertex grid and emits the
   * piecewise-linear surface the GPU renders (matches the published mesh exactly).
   * When 0, raw bilinear sampling — fine for noise-only scenes.
   */
  cellSize?: number;
  /** Tile geometry parameters. Required when `cellSize > 0`. */
  tileWorldSize?: number;
  tilesX?: number;
  tilesZ?: number;
}

/**
 * Heightmap-PNG-driven HeightField. Bilinearly samples the red channel; when a
 * per-tile cell size is supplied, snaps to the vertex grid and uses the same
 * triangulated linear combo as the rendered terrain (matches `gbuffer-terrain`
 * geometry byte-for-byte at vertex positions, sub-cell-precise at fragments).
 *
 * Extracted from `TiledTerrainStreamer.sampleHeight` so it can serve both
 * runtime physics and editor authoring through the unified HeightField API.
 */
export class HeightmapHeightField implements HeightField {
  private readonly hm: HeightmapGrid;
  private readonly amplitude: number;
  private readonly worldMinX: number;
  private readonly worldMinZ: number;
  private readonly worldWidth: number;
  private readonly worldDepth: number;
  private readonly cellSize: number;
  private readonly tileWorldSize: number;
  private readonly tilesX: number;
  private readonly tilesZ: number;
  private readonly normalEps: number;

  constructor(config: HeightmapHeightFieldConfig) {
    this.hm = config.heightmap;
    this.amplitude = config.amplitude;
    this.worldMinX = config.worldMinX;
    this.worldMinZ = config.worldMinZ;
    this.worldWidth = Math.max(1, config.worldWidth);
    this.worldDepth = Math.max(1, config.worldDepth);
    this.cellSize = config.cellSize ?? 0;
    this.tileWorldSize = config.tileWorldSize ?? this.worldWidth;
    this.tilesX = config.tilesX ?? 1;
    this.tilesZ = config.tilesZ ?? 1;
    // Pixel-aligned eps: half a cell when meshAligned, else half a heightmap pixel.
    const pxStepX = this.worldWidth / Math.max(1, this.hm.width - 1);
    this.normalEps = Math.max(0.25, this.cellSize > 0 ? this.cellSize * 0.5 : pxStepX * 0.5);
  }

  sampleHeight(x: number, z: number): number {
    if (this.cellSize > 0) return this.sampleMeshAligned(x, z);
    return this.sampleBilinear(x, z);
  }

  sampleHeightWithNormal(x: number, z: number): HeightFieldSample {
    return centralDifferenceNormal(x, z, this.normalEps, (sx, sz) =>
      this.sampleHeight(sx, sz),
    )!;
  }

  raycast(ray: Ray, maxDistance?: number): HeightFieldHit | null {
    return raycastHeightField(
      ray,
      (x, z) => this.sampleHeight(x, z),
      (x, z) => this.sampleHeightWithNormal(x, z),
      maxDistance,
      Math.max(2, this.normalEps * 4),
    );
  }

  private sampleBilinear(x: number, z: number): number {
    const hm = this.hm;
    const u = clamp01((x - this.worldMinX) / this.worldWidth);
    const v = clamp01((z - this.worldMinZ) / this.worldDepth);
    const px = u * (hm.width - 1);
    const py = v * (hm.height - 1);
    const x0 = Math.floor(px);
    const y0 = Math.floor(py);
    const x1 = Math.min(x0 + 1, hm.width - 1);
    const y1 = Math.min(y0 + 1, hm.height - 1);
    const fx = px - x0;
    const fy = py - y0;
    const r00 = hm.data[(y0 * hm.width + x0) * 4] / 255;
    const r10 = hm.data[(y0 * hm.width + x1) * 4] / 255;
    const r01 = hm.data[(y1 * hm.width + x0) * 4] / 255;
    const r11 = hm.data[(y1 * hm.width + x1) * 4] / 255;
    const gray = (r00 * (1 - fx) + r10 * fx) * (1 - fy) + (r01 * (1 - fx) + r11 * fx) * fy;
    return gray * this.amplitude;
  }

  private sampleMeshAligned(x: number, z: number): number {
    const cs = this.cellSize;
    const worldMaxX = this.worldMinX + this.worldWidth;
    const worldMaxZ = this.worldMinZ + this.worldDepth;

    const tx = clamp(Math.floor((x - this.worldMinX) / this.tileWorldSize), 0, this.tilesX - 1);
    const tz = clamp(Math.floor((z - this.worldMinZ) / this.tileWorldSize), 0, this.tilesZ - 1);

    const tileMinX = this.worldMinX + tx * this.tileWorldSize;
    const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
    const tileMaxX = Math.min(tileMinX + this.tileWorldSize, worldMaxX);
    const tileMaxZ = Math.min(tileMinZ + this.tileWorldSize, worldMaxZ);
    const tileWidth = tileMaxX - tileMinX;
    const tileDepth = tileMaxZ - tileMinZ;

    const gridW = Math.max(1, Math.trunc(tileWidth / cs + 0.5));
    const gridH = Math.max(1, Math.trunc(tileDepth / cs + 0.5));
    const cellSizeX = tileWidth / gridW;
    const cellSizeZ = tileDepth / gridH;

    const lx = clamp(x - tileMinX, 0, tileWidth);
    const lz = clamp(z - tileMinZ, 0, tileDepth);
    const gridX = lx / cellSizeX;
    const gridZ = lz / cellSizeZ;
    const col = clamp(Math.floor(gridX), 0, gridW - 1);
    const row = clamp(Math.floor(gridZ), 0, gridH - 1);
    const fu = clamp01(gridX - col);
    const fv = clamp01(gridZ - row);

    const vx0 = tileMinX + col * cellSizeX;
    const vx1 = tileMinX + (col + 1) * cellSizeX;
    const vz0 = tileMinZ + row * cellSizeZ;
    const vz1 = tileMinZ + (row + 1) * cellSizeZ;

    const y00 = this.sampleBilinear(vx0, vz0);
    const y10 = this.sampleBilinear(vx1, vz0);
    const y01 = this.sampleBilinear(vx0, vz1);
    const y11 = this.sampleBilinear(vx1, vz1);

    if (fu + fv <= 1) {
      return (1 - fu - fv) * y00 + fv * y01 + fu * y10;
    }
    return (1 - fv) * y10 + (1 - fu) * y01 + (fu + fv - 1) * y11;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
